import {calculateIndirectFinancedAmount, getAmortizationSchedule, getIndirectUnpaidBalance} from "../../../utils/quoteUtils";
import {IndirectTransactionType, IQuote} from "../../../Interfaces/Common/IQuote";
import {IQuotePayType} from "../../../Interfaces/Common/IQuotePayType";
import {IApplication} from "../../../Interfaces/Application/IApplication";
import {Constants} from "../../../utils/constants";
import {IApplicationQuote} from "../../../Interfaces/Application/IApplicationQuote";
import {IndirectQuoteType} from "../../../Data/IndirectQuoteType";
import {
  IndirectQuoterColumnConfig, IndirectQuoterFieldLabel,
  IndirectQuoterInputType
} from "../../../Interfaces/IndirectQuoter/IndirectQuoterFieldConfig";
import {getHelperText} from "../../Views/OrderEditor/OverviewHelpers";
import {IPricingProgram} from "../../../Interfaces/Common/IPricingProgram";
import {Organization} from "@amsanalytics/react-shared-library/build/Models/Organization";
import {ApplicationQuoteType, divide, hasValue, multiply, Vehicle} from "@amsanalytics/react-shared-library";
import {ApplicationFunding} from "../../../Interfaces/Application/ApplicationFunding";
import {IConfiguration} from "../../../Data/ConfigurationData";

// The amount-financed formula lives in quoteUtils so every quoter shares one implementation. Import it from there:
// quoteUtils also imports this module, and re-exporting its bindings here makes React Refresh's export scan read
// them before quoteUtils has finished evaluating ("Cannot access ... before initialization" on startup).

export interface IndirectQuoteTransactionTypeOption {
  id: IndirectTransactionType;
  name: string;
}

export const indirectQuoteTransactionTypes: IndirectQuoteTransactionTypeOption[] = [
  { id: IndirectTransactionType.Retail, name: 'Retail' },
  { id: IndirectTransactionType.PaymentCall, name: 'Payment Call' }
];

export const calculateIndirectBalloonPayment = (quote: IQuote): number => {
  if (!quote || !quote.term) {
    return 0;
  }

  const amortizationSchedule = getAmortizationSchedule(quote);
  const balloonPaymentNumber = quote.term - 1; // since 0 indexed a term of 48 would mean 48th payment is in index 47 and 49th(balloon) is in index 49
  if (amortizationSchedule.length <= balloonPaymentNumber + 1) return 0;
  return amortizationSchedule[balloonPaymentNumber].endingPrincipal + amortizationSchedule[balloonPaymentNumber + 1].interest;
};

export const getPrimaryApplicantIncomeAndDebtInfo = (currentApp: IApplication | undefined) => {
  return currentApp?.appCustomers
    ?.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId)
    ?.customer?.customerIncomeDebt[0];
}

export const getAppQuoteByType = (currentApp: IApplication | undefined, quoteType: IndirectQuoteType): IApplicationQuote | undefined => {
  return currentApp?.applicationQuotes?.find(aq => aq.quote.quoteType === quoteType);
}

export const getIndirectDiscountPercentage = (discountAmount: number, salePrice: number): number => {
  return divide(discountAmount, salePrice);
}

const getFrontEndLtvNumerator = (quote: IQuote): number => {
  return getIndirectUnpaidBalance(quote) - (quote.discount ?? 0);
}

export const getIndirectFrontEndLtv = (quote: IQuote, vehicle: Vehicle | undefined): number => {
  const frontEndLtvNumerator = getFrontEndLtvNumerator(quote);
  return divide(frontEndLtvNumerator, vehicle?.bookoutValue ?? 0);
}

export const getIndirectFrontEndLtvAfterPartial = (quote: IQuote, vehicle: Vehicle | undefined): number => {
  const frontEndLtvNumeratorWithoutDealerPartial = getFrontEndLtvNumerator(quote) - (quote.dealerPartialAmount ?? 0);
  return divide(frontEndLtvNumeratorWithoutDealerPartial, vehicle?.bookoutValue ?? 0);
}

export const getIndirectTotalLtv = (quote: IQuote, vehicle: Vehicle | undefined): number => {
  return divide(quote.amountToFinance ?? 0, vehicle?.bookoutValue ?? 0);
}

export const getDealerReserve = (quote: IQuote): number | undefined => {
  return divide(multiply((quote.dealerPartialAmount ?? 0), (quote.dealerPartialPercent ?? 0)), 100, 2);
}

export const getDealerCheck = (quote: IQuote): number | undefined => {
  // If they typed in a dealer reserve override amount, then we will use that as the input for the dealer check calculation
  const reserveAmountToUse = (quote.dealerReserveOverride ?? 0) > 0
    ? quote.dealerReserveOverride
    : quote.dealerReserve;

  return (quote.amountToFinance ?? 0)
    - (quote.bankFee ?? 0)
    - (quote.lenderFees ?? 0)
    - (quote.discount ?? 0)
    - (reserveAmountToUse ?? 0)
    + (quote.commission ?? 0);
}

export const getAmsCodeForReserve = (currentApp: IApplication | undefined, organizations: Organization[]): string | undefined => {
  if (!currentApp || organizations.length === 0) {
    return undefined;
  }

  const organizationForApp = organizations.find(o => o.id === currentApp.organizationId);
  if (!organizationForApp) {
    return undefined;
  }

  if ((currentApp.quoteType !== ApplicationQuoteType.Indirect)) {
    return organizationForApp.amsCode;
  }

  const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);
  if (!underwriterAppQuote) {
    return undefined;
  }

  const reserveAmountToUse = (underwriterAppQuote.quote.dealerReserveOverride ?? 0) > 0
    ? underwriterAppQuote.quote.dealerReserveOverride ?? 0
    : underwriterAppQuote.quote.dealerReserve ?? 0;

  return reserveAmountToUse > 0
    ? hasValue(organizationForApp.reserveAmsCode) ? organizationForApp.reserveAmsCode : organizationForApp.amsCode // Falls back on the regular AMS code if there is no reserve code
    : organizationForApp.amsCode;
}

export const getDealerRequestedColumnConfig = (
  currentApp: IApplication | undefined,
  enableDealerFlowFields: boolean
): IndirectQuoterColumnConfig => {
  const dealerRequestedApplicationQuote = getAppQuoteByType(currentApp, IndirectQuoteType.DealerRequested);
  const dealerRequestedQuote = dealerRequestedApplicationQuote?.quote;
  const appIsDealerFlow = currentApp?.appSource?.toUpperCase() === 'DEALER FLOW';
  const disableInput = !appIsDealerFlow || !enableDealerFlowFields;

  const columnConfig: IndirectQuoterColumnConfig = {
    columnTitle: `${currentApp?.appSource} Request`,
    editableApplicationQuote: dealerRequestedApplicationQuote,
    fieldConfigs: [],
    hideAllRecommendedValues: true // Always true for the dealer requested
  };

  if (!currentApp || !dealerRequestedQuote) {
    return columnConfig;
  }

  columnConfig.fieldConfigs = [
    {
      fieldLabel: 'Sale Price',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.carSalePrice,
      fieldName: 'carSalePrice',
      recommendedValue: undefined,
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Bookout',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.nadavalue,
      fieldName: 'nadavalue',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Cash Down',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.cashDown,
      fieldName: 'cashDown',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Deferred Down',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.deferredDown,
      fieldName: 'deferredDown',
      disableInput: disableInput,
      hideRecommendation: true,
      min: 0
    },
    {
      fieldLabel: 'Rebate',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.rebate,
      fieldName: 'rebate',
      disableInput: disableInput,
      hideRecommendation: true
    },
    // {
    //   fieldLabel: 'Trade Allowance',
    //   inputType: IndirectQuoterInputType.Currency,
    //   value: dealerRequestedQuote.tradeAllowance,
    //   fieldName: 'tradeAllowance',
    //   disableInput: disableInput,
    //   hideRecommendation: true
    // },
    // {
    //   fieldLabel: 'Trade Lien',
    //   inputType: IndirectQuoterInputType.Currency,
    //   value: dealerRequestedQuote.tradeLien,
    //   fieldName: 'tradeLien',
    //   disableInput: disableInput,
    //   hideRecommendation: true
    // },
    {
      fieldLabel: 'Net Trade',
      inputType: IndirectQuoterInputType.Currency,
      // value: dealerRequestedQuote.tradeLien,
      value: currentApp?.appSource === 'Dealer Track'
        ? dealerRequestedQuote.tradeAllowance
        : (dealerRequestedQuote?.tradeAllowance ?? 0) - (dealerRequestedQuote?.tradeLien ?? 0),
      fieldName: 'tradeAllowance',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Taxes',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.taxes,
      fieldName: 'taxes',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Front End Fees',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.docFee,
      fieldName: 'docFee',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'TT&L',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.taxTitleAndLicense,
      fieldName: 'taxTitleAndLicense',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Unpaid Balance',
      inputType: IndirectQuoterInputType.Currency,
      value: getIndirectUnpaidBalance(dealerRequestedQuote),
      disableInput: true,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Service Warranty',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.serviceContract,
      fieldName: 'serviceContract',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'GAP',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.gapInsurance,
      fieldName: 'gapInsurance',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Credit Life',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.creditLifeInsurance,
      fieldName: 'creditLifeInsurance',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Disability / AH',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.disabilityInsurance,
      fieldName: 'disabilityInsurance',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Other Back End Fees',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.other,
      fieldName: 'other',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Amount to Finance',
      inputType: IndirectQuoterInputType.Currency,
      value: dealerRequestedQuote.amountToFinance,
      fieldName: 'amountToFinance',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'APR',
      inputType: IndirectQuoterInputType.Percentage,
      value: dealerRequestedQuote.apr,
      fieldName: 'apr',
      disableInput: disableInput,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Term (months)',
      inputType: IndirectQuoterInputType.Integer,
      value: dealerRequestedQuote.term,
      fieldName: 'term',
      disableInput: disableInput,
      hideRecommendation: true
    }
  ];

  return columnConfig;
}

export const getUnderwriterDecisionColumnConfig = (
  currentApp: IApplication | undefined,
  payTypes: IQuotePayType[],
  hideAllRecommendedValues: boolean,
  allowPaymentEdit: boolean,
  allowTermEdit: boolean,
  allowRateEdit: boolean
): IndirectQuoterColumnConfig => {
  const calculatedAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Recommended);
  const calculatedQuote = calculatedAppQuote?.quote;
  const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);
  const underwriterQuote = underwriterAppQuote?.quote;

  const columnConfig: IndirectQuoterColumnConfig = {
    columnTitle: "Loan Quote",
    editableApplicationQuote: underwriterAppQuote,
    recommendedApplicationQuote: underwriterAppQuote,
    fieldConfigs: [],
    hideAllRecommendedValues
  };

  if (!underwriterQuote || !calculatedQuote) {
    return columnConfig;
  }

  const disablePayment = !allowPaymentEdit;
  const disableTerm = !allowTermEdit && allowPaymentEdit;
  const disableRate = !allowRateEdit && allowPaymentEdit;

  columnConfig.fieldConfigs = [
    {
      fieldLabel: 'Sale Price',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.carSalePrice,
      fieldName: 'carSalePrice',
      disableInput: false,
      hideRecommendation: true,
      errorMessage: getHelperText('carSalePrice', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Bookout',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote?.nadavalue,
      fieldName: 'nadavalue',
      recommendedValue: calculatedQuote?.nadavalue,
      disableInput: false,
      hideRecommendation: false,
      errorMessage: getHelperText('nadavalue', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Cash Down',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.cashDown,
      fieldName: 'cashDown',
      recommendedValue: calculatedQuote.cashDown,
      disableInput: false,
      hideRecommendation: false,
      errorMessage: getHelperText('cashDown', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Deferred Down',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.deferredDown,
      fieldName: 'deferredDown',
      disableInput: false,
      hideRecommendation: true, // The recommended quote does not calculate a deferred down
      min: 0,
      errorMessage: getHelperText('deferredDown', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Rebate',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.rebate,
      fieldName: 'rebate',
      disableInput: false,
      hideRecommendation: true,
      errorMessage: getHelperText('discount', underwriterAppQuote, currentApp)
    },
    // {
    //   fieldLabel: 'Trade Allowance',
    //   inputType: IndirectQuoterInputType.Currency,
    //   value: underwriterQuote.tradeAllowance,
    //   fieldName: 'tradeAllowance',
    //   disableInput: false,
    //   hideRecommendation: true,
    //   errorMessage: getHelperText('tradeAllowance', underwriterAppQuote, currentApp)
    // },
    // {
    //   fieldLabel: 'Trade Lien',
    //   inputType: IndirectQuoterInputType.Currency,
    //   value: underwriterQuote.tradeLien,
    //   fieldName: 'tradeLien',
    //   disableInput: false,
    //   hideRecommendation: true,
    //   errorMessage: getHelperText('tradeLien', underwriterAppQuote, currentApp)
    // },
    {
      fieldLabel: 'Net Trade',
      inputType: IndirectQuoterInputType.Currency,
      // value: dealerRequestedQuote.tradeLien,
      value: currentApp?.appSource === 'Dealer Track'
        ? underwriterQuote.tradeAllowance
        : (underwriterQuote?.tradeAllowance ?? 0) - (underwriterQuote?.tradeLien ?? 0),
      fieldName: 'tradeAllowance',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Taxes',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.taxes,
      fieldName: 'taxes',
      disableInput: false,
      hideRecommendation: true,
      errorMessage: getHelperText('taxes', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Front End Fees',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.docFee,
      fieldName: 'docFee',
      recommendedValue: 0,
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'TT&L',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.taxTitleAndLicense,
      fieldName: 'taxTitleAndLicense',
      recommendedValue: underwriterQuote.taxTitleAndLicense,
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Unpaid Balance',
      inputType: IndirectQuoterInputType.Currency,
      value: getIndirectUnpaidBalance(underwriterQuote),
      disableInput: true,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Service Warranty',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.serviceContract,
      fieldName: 'serviceContract',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'GAP',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.gapInsurance,
      fieldName: 'gapInsurance',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Credit Life',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.creditLifeInsurance,
      fieldName: 'creditLifeInsurance',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Disability / AH',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.disabilityInsurance,
      fieldName: 'disabilityInsurance',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Other Back End Fees',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.other,
      fieldName: 'other',
      recommendedValue: undefined,
      disableInput: false,
      hideRecommendation: true,
      errorMessage: getHelperText('other', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Amount to Finance',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.amountToFinance,
      fieldName: 'amountToFinance',
      recommendedValue: calculatedQuote.amountToFinance,
      disableInput: true,
      hideRecommendation: false,
      errorMessage: getHelperText('amountToFinance', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'APR',
      inputType: IndirectQuoterInputType.Percentage,
      value: underwriterQuote.apr,
      fieldName: 'apr',
      recommendedValue: calculatedQuote.apr,
      disableInput: disableRate,
      hideRecommendation: false,
      errorMessage: getHelperText('apr', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Term (months)',
      inputType: IndirectQuoterInputType.Integer,
      value: underwriterQuote.term,
      fieldName: 'term',
      recommendedValue: calculatedQuote.term,
      disableInput: disableTerm,
      hideRecommendation: false,
      errorMessage: getHelperText('term', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Balloon Term (months)',
      inputType: IndirectQuoterInputType.Integer,
      value: underwriterQuote.balloonTerm,
      fieldName: 'balloonTerm',
      recommendedValue: calculatedQuote.balloonTerm,
      disableInput: false,
      hideRecommendation: false
    },
    {
      fieldLabel: 'Pay Type',
      inputType: IndirectQuoterInputType.Dropdown,
      value: underwriterQuote.quotePayTypeId,
      fieldName: 'quotePayTypeId',
      recommendedValue: calculatedQuote.quotePayTypeId,
      disableInput: false,
      hideRecommendation: false,
      lookups: {
        values: payTypes,
        idField: 'id',
        descriptionField: 'description'
      }
    },
    {
      fieldLabel: 'Payment',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.payment,
      fieldName: 'payment',
      recommendedValue: calculatedQuote.payment,
      disableInput: disablePayment,
      hideRecommendation: true,
      errorMessage: getHelperText('monthlyPayment',underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Balloon Payment',
      inputType: IndirectQuoterInputType.Currency,
      value: calculateIndirectBalloonPayment(underwriterQuote),
      recommendedValue: calculateIndirectBalloonPayment(calculatedQuote),
      disableInput: true,
      hideRecommendation: true
    }
  ];

  return columnConfig;
};

export const getDealerCheckColumnConfig = (
  currentApp: IApplication | undefined,
  pricingPrograms: IPricingProgram[] | undefined,
  organizations: Organization[],
  hidePricingProgram: boolean,
  hideAllRecommendedValues: boolean,
  allowCalculatedTierOverride: boolean,
  useRetailDealerFlow: boolean
): IndirectQuoterColumnConfig => {
  const calculatedAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Recommended);
  const calculatedQuote = calculatedAppQuote?.quote;
  const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);
  const underwriterQuote = underwriterAppQuote?.quote;

  const columnConfig: IndirectQuoterColumnConfig = {
    columnTitle: "Dealer Check",
    editableApplicationQuote: underwriterAppQuote,
    recommendedApplicationQuote: underwriterAppQuote,
    fieldConfigs: [],
    hideAllRecommendedValues: hideAllRecommendedValues
  };

  if (!underwriterQuote || !calculatedQuote) {
    return columnConfig;
  }

  const amsCodeForReserve = getAmsCodeForReserve(currentApp, organizations);
  
  columnConfig.fieldConfigs = [
    {
      fieldLabel: 'Transaction Type',
      inputType: IndirectQuoterInputType.Dropdown,
      value: underwriterQuote.transactionType,
      fieldName: 'transactionType',
      recommendedValue: undefined,
      disableInput: false,
      hideRecommendation: true,
      lookups: {
        values: indirectQuoteTransactionTypes,
        idField: 'id',
        descriptionField: 'name'
      }
    },
    {
      fieldLabel: 'Pricing Program',
      inputType: IndirectQuoterInputType.Dropdown,
      value: underwriterQuote.pricingProgramId,
      fieldName: 'pricingProgramId',
      recommendedValue: calculatedQuote.pricingProgramId,
      disableInput: false,
      hideRecommendation: false,
      lookups: {
        values: pricingPrograms,
        idField: 'id',
        descriptionField: 'name'
      }
    },
    {
      fieldLabel: 'Front End LTV',
      inputType: IndirectQuoterInputType.Text,
      value: `${(getIndirectFrontEndLtv(underwriterQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      recommendedValue: `${(getIndirectFrontEndLtv(calculatedQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      disableInput: true,
      hideRecommendation: false,
      errorMessage: getHelperText('ltv', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Front End LTV After Dealer Partial',
      inputType: IndirectQuoterInputType.Text,
      value: `${(getIndirectFrontEndLtvAfterPartial(underwriterQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      recommendedValue: `${(getIndirectFrontEndLtvAfterPartial(calculatedQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      disableInput: true,
      hideRecommendation: false,
      errorMessage: getHelperText('ltv', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Total LTV',
      inputType: IndirectQuoterInputType.Text,
      value: `${(getIndirectTotalLtv(underwriterQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      recommendedValue: `${(getIndirectTotalLtv(calculatedQuote, currentApp?.vehicle) * 100).toFixed(2)}%`,
      disableInput: true,
      hideRecommendation: false,
      errorMessage: getHelperText('ltv', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Acquisition Fee',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.bankFee,
      fieldName: 'bankFee',
      recommendedValue: calculatedQuote.bankFee,
      disableInput: false,
      hideRecommendation: false
    },
    {
      fieldLabel: 'Other Lender Fees',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.lenderFees,
      fieldName: 'lenderFees',
      recommendedValue: calculatedQuote.lenderFees,
      disableInput: false,
      hideRecommendation: false,
      errorMessage: getHelperText('lenderFees', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Discount %',
      inputType: IndirectQuoterInputType.Percentage,
      value: getIndirectDiscountPercentage(underwriterQuote.discount ?? 0, underwriterQuote.carSalePrice ?? 0),
      recommendedValue: getIndirectDiscountPercentage(calculatedQuote.discount ?? 0, calculatedQuote.carSalePrice ?? 0),
      disableInput: true,
      hideRecommendation: false,
      errorMessage: getHelperText('discount', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Discount $',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.discount,
      fieldName: 'discount',
      disableInput: false,
      hideRecommendation: true,
      errorMessage: getHelperText('discount', underwriterAppQuote, currentApp)
    },
    {
      fieldLabel: 'Dealer Partial',
      fieldLabelAddOn: `${(underwriterQuote.dealerPartialPercent ?? 0).toFixed(2)}%`,
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.dealerPartialAmount,
      fieldName: 'dealerPartialAmount',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Dealer Reserve',
      fieldLabelAddOn: amsCodeForReserve,
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.dealerReserve,
      fieldName: 'dealerReserve',
      disableInput: true,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Dealer Reserve Override',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.dealerReserveOverride,
      fieldName: 'dealerReserveOverride',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Commission',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.commission,
      fieldName: 'commission',
      disableInput: false,
      hideRecommendation: true
    },
    {
      fieldLabel: 'Dealer Check',
      inputType: IndirectQuoterInputType.Currency,
      value: underwriterQuote.dealerCheck,
      fieldName: 'dealerCheck',
      disableInput: true,
      hideRecommendation: true
    }
  ];
  
  const fieldsToRemove: IndirectQuoterFieldLabel[] = [];

  if (hidePricingProgram) {
    fieldsToRemove.push('Pricing Program');
  }
  
  if (!useRetailDealerFlow) {
    fieldsToRemove.push('Transaction Type');
  }

  columnConfig.fieldConfigs = columnConfig.fieldConfigs.filter(fc => !fieldsToRemove.includes(fc.fieldLabel));

  return columnConfig;
};

// Returns true if the difference between disclosureAmountFinanced and the amountFinanced is within 200.
export const isAmountFinancedCompliant = (underwriterQuote: IQuote, funding: ApplicationFunding | undefined): boolean => {
  return underwriterQuote.amountToFinance !== undefined &&
    funding?.disclosureAmountFinanced !== undefined &&
    Math.abs(underwriterQuote.amountToFinance - funding.disclosureAmountFinanced) <= 200;
};

// Returns true if the difference between the underwriterQuote APR and the Disclosure APR is within 0.125%.
export const isAprCompliant = (underwriterApr: number | undefined, disclosureApr: number | undefined, configuration: IConfiguration): boolean => {
  return underwriterApr !== undefined &&
    disclosureApr !== undefined &&
    Math.abs(underwriterApr - disclosureApr) <= configuration.applicationFundingAprComplianceRange;
};

// Returns true if the difference between the Carleton APR and the Disclosure APR is within 0.125%.
export const isCarletonAprCompliant = (funding: ApplicationFunding | undefined, configuration: IConfiguration): boolean => {
  return hasValue(funding?.carletonCalculatedApr) &&
    funding?.disclosureApr !== undefined &&
    Math.abs(funding.carletonCalculatedApr! - funding.disclosureApr) <= configuration.applicationFundingAprComplianceRange;
};

// Returns true if the disclosure amount financed is equal to the amountFinanced.
export const areAmountFinancedValuesEqual = (funding: ApplicationFunding | undefined): boolean => {
  return funding?.disclosureAmountFinanced !== undefined &&
    funding.amountFinanced === funding.disclosureAmountFinanced;
};